import { Popover as BasePopover } from '@base-ui/react/popover';
import type { HTMLAttributes, ReactNode } from 'react';
import { useLocalization } from '@/i18n';
import { useTraitDefinition } from '@/hooks/useTraitDefinition';
import { useEncounterRollout } from '@/config/encounterRollout';
import OrcRichText from '../OrcRichText';
import { EnglishName } from '../CharacterWizard/EnglishName';
import { cx } from './utils';

export type Pf2eBadgeKind = 'trait' | 'rarity' | 'level';
export type Pf2eRarity = 'common' | 'uncommon' | 'rare' | 'unique';

export interface Pf2eBadgeProps extends HTMLAttributes<HTMLSpanElement> {
  kind?: Pf2eBadgeKind;
  rarity?: Pf2eRarity;
  trait?: string;
  interactiveTrait?: boolean;
}

export function Pf2eBadge({
  className,
  kind = 'trait',
  rarity,
  trait,
  interactiveTrait = true,
  children,
  ...props
}: Pf2eBadgeProps) {
  const { traitPopoverEnabled } = useEncounterRollout();
  const lookupLabel = trait || (typeof children === 'string' || typeof children === 'number'
    ? String(children)
    : '');
  if (kind === 'trait' && interactiveTrait && traitPopoverEnabled && lookupLabel) {
    return (
      <TraitPopoverBadge
        className={className}
        label={lookupLabel}
        rarity={rarity}
        visualProps={props}
      >
        {children}
      </TraitPopoverBadge>
    );
  }
  return <BadgeVisual className={className} kind={kind} rarity={rarity} {...props}>{children}</BadgeVisual>;
}

function BadgeVisual({
  className,
  kind = 'trait',
  rarity,
  children,
  ...props
}: Pf2eBadgeProps) {
  return (
    <span
      className={cx(
        'ui-pf2e-badge',
        `ui-pf2e-badge--${kind}`,
        kind === 'rarity' && rarity ? `ui-pf2e-badge--${rarity}` : null,
        className,
      )}
      {...props}
    >
      {children}
    </span>
  );
}

function TraitPopoverBadge({
  children,
  className,
  label,
  visualProps,
}: {
  children: ReactNode;
  className?: string;
  label: string;
  rarity?: Pf2eRarity;
  visualProps: HTMLAttributes<HTMLSpanElement>;
}) {
  const { t } = useLocalization();
  const { loading, trait, error } = useTraitDefinition(label);
  const title = trait?.localized_name || trait?.name || label;
  const description = trait?.localized_description || trait?.description || '';
  const childLabel = typeof children === 'string' || typeof children === 'number'
    ? String(children).trim().toLocaleLowerCase()
    : '';
  const replaceCanonicalLabel = Boolean(
    trait?.localized_name
    && (!childLabel || childLabel === label.toLocaleLowerCase() || childLabel === trait.name.toLocaleLowerCase()),
  );

  return (
    <BasePopover.Root>
      <BasePopover.Trigger
        className="ui-trait-popover__trigger"
        render={<span role="button" tabIndex={0} />}
        nativeButton={false}
        aria-label={t('traits.popover.open', { trait: title }, 'View {{trait}} trait')}
      >
        <BadgeVisual className={className} kind="trait" {...visualProps}>
          {replaceCanonicalLabel ? trait?.localized_name : children}
        </BadgeVisual>
      </BasePopover.Trigger>
      <BasePopover.Portal>
        <BasePopover.Positioner
          key={`${loading ? 'loading' : trait?.id || 'unavailable'}:${description.length}`}
          className="ui-positioner ui-positioner--topmost"
          collisionAvoidance={{ side: 'shift', align: 'shift', fallbackAxisSide: 'none' }}
          collisionPadding={8}
          sideOffset={8}
        >
          <BasePopover.Popup
            className="ui-trait-popover__popup"
            initialFocus={false}
          >
            <BasePopover.Title className="ui-trait-popover__title">
              <span>{title}</span>
              {trait ? (
                <EnglishName
                  englishName={trait.name}
                  displayedName={title}
                  className="ui-trait-popover__english-name"
                />
              ) : null}
            </BasePopover.Title>
            <div className="ui-trait-popover__body">
              {loading ? (
                <p
                  className="ui-trait-popover__status"
                  role="status"
                  aria-live="polite"
                >
                  {t('traits.popover.loading', {}, 'Loading trait description…')}
                </p>
              ) : trait ? (
                <>
                  <span
                    className="ui-sr-only"
                    role="status"
                    aria-live="polite"
                    aria-atomic="true"
                  >
                    {t(
                      'traits.popover.loaded',
                      { trait: title },
                      'Opened the {{trait}} trait description.',
                    )}
                  </span>
                  <div className="ui-trait-popover__description">
                    <OrcRichText content={description} html />
                  </div>
                </>
              ) : (
                <p
                  className="ui-trait-popover__status"
                  role="status"
                  aria-live="polite"
                >
                  {error
                    ? t('traits.popover.error', {}, 'The trait description could not be loaded.')
                    : t('traits.popover.unavailable', {}, 'No published ORC description is available for this trait.')}
                </p>
              )}
            </div>
            <BasePopover.Arrow className="ui-trait-popover__arrow" />
          </BasePopover.Popup>
        </BasePopover.Positioner>
      </BasePopover.Portal>
    </BasePopover.Root>
  );
}
